import { MessagePayload } from '@m43/sockets-mark43';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { EventPanelResource, EventPanelView } from '@mark43/cad-api';
import { useCallback } from 'react';
import { useApiClient } from '~/api-client';
import { StreamMap } from '../../socket/stream-types';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';

const streamsToSubscribeTo = ['nudge'] as const;
export function useIntegratedEventPanelViewQuery(props: {
    eventId: number | undefined;
    query: UseQueryResult<EventPanelView | undefined, unknown>;
    onUpdate?: (eventPanelView: EventPanelView) => void;
    enabled?: boolean;
}) {
    const { query, onUpdate, eventId } = props;
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    const fetchEventForRealtimeUpdate = useCallback(
        (inputs: number[]) => {
            if (typeof inputs[0] === 'number') {
                return apiClient
                    .request<EventPanelResource.GetEventPanelViewPreventSessionExtension>({
                        method: 'POST',
                        url: 'event_panel/prevent_session_extension/',
                        data: {
                            metadata: true,
                            form: true,
                            configuration: false,
                            options: false,
                            eventId: inputs[0],
                        },
                    })
                    .then((data) => {
                        const eventPanelView = data.data.data;
                        if (eventPanelView) {
                            onUpdate?.(eventPanelView);
                            queryClient.setQueryData(
                                ['event-panel-view', eventPanelView.form.eventId],
                                eventPanelView
                            );
                        }
                    });
            }
            return Promise.resolve();
        },
        [apiClient, queryClient, onUpdate]
    );

    const onMessage: (message: MessagePayload<StreamMap, 'nudge'>) => number[] = useCallback(
        (message) => {
            if (!eventId) return [];
            const scheduledEventNudgeKeys = ['SAE'];
            const scheduledEventRelatedNudgeKeys = ['SET', 'SESEL'];
            const eventNudgeKeys = ['E', 'EC'];
            const eventRelatedNudgeKeys = ['IEL', 'AERC', 'USS'];
            const renNudgeKeys = ['REN'];

            // If the rens associated with our event change,
            // refetch the metadata
            for (const key of renNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (entry.e === eventId) {
                        return [eventId];
                    }
                }
            }

            for (const key of eventNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (entry.id === eventId) {
                        return [eventId];
                    }
                }
            }

            for (const key of eventRelatedNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (entry.e === eventId) {
                        return [eventId];
                    }
                }
            }

            for (const key of scheduledEventNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (entry.id === eventId) {
                        return [eventId];
                    }
                }
            }

            for (const key of scheduledEventRelatedNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (entry.se === eventId) {
                        return [eventId];
                    }
                }
            }
            return [];
        },
        [eventId]
    );

    useIntegratedStreams({
        streamNames: streamsToSubscribeTo,
        promiseBufferCallback: fetchEventForRealtimeUpdate,
        onMessage,
        query,
        enabled: props.enabled,
    });
}
